export class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ApiError";
  }
}

let forgePromise: Promise<boolean> | null = null;
let bridgeModule: Promise<typeof import("@forge/bridge")> | null = null;

function bridgeInstalled(): boolean {
  const candidate = (globalThis as { __bridge?: { callBridge?: unknown } }).__bridge;
  return typeof candidate?.callBridge === "function";
}

function loadBridge() {
  bridgeModule ??= import("@forge/bridge");
  return bridgeModule;
}

async function insideForge(): Promise<boolean> {
  if (forgePromise) return forgePromise;
  // Jira injects window.__bridge before the app script runs. Detect it
  // synchronously instead of racing getContext() against a short timeout,
  // which could fall back to fetch() against the CDN origin inside Jira.
  forgePromise = Promise.resolve(bridgeInstalled());
  return forgePromise;
}

export async function forgeContext(): Promise<Record<string, unknown> | null> {
  if (!(await insideForge())) return null;
  try {
    const bridge = await loadBridge();
    return (await bridge.view.getContext()) as unknown as Record<string, unknown>;
  } catch {
    return null;
  }
}

export async function openFullApp(projectKey?: string): Promise<void> {
  if (await insideForge()) {
    try {
      const bridge = await loadBridge();
      await bridge.router.navigate({
        target: "module",
        moduleKey: "tcm-project-workspace",
        ...(projectKey ? { projectKey } : {}),
      });
      return;
    } catch {
      /* Fall through to the hash route when the bridge cannot navigate. */
    }
  }
  window.location.hash = "/";
}

const getCache = new Map<string, { at: number; body: unknown }>();
const GET_TTL_MS = 20_000;

export function invalidateApiCache(): void {
  getCache.clear();
}

function rememberGet<T>(path: string, method: string, body: T): T {
  if (method === "GET") getCache.set(path, { at: Date.now(), body });
  else invalidateApiCache();
  return body;
}

function cachedGet<T>(path: string, method: string): T | undefined {
  if (method !== "GET") return undefined;
  const hit = getCache.get(path);
  if (!hit || Date.now() - hit.at > GET_TTL_MS) return undefined;
  return hit.body as T;
}

function noteTiming(path: string, headers: unknown) {
  let timing = "";
  if (headers instanceof Headers) timing = headers.get("server-timing") ?? "";
  else if (headers && typeof headers === "object") {
    const record = headers as Record<string, string>;
    timing = record["server-timing"] ?? record["Server-Timing"] ?? "";
  }
  if (timing) console.info(JSON.stringify({ msg: "northline-timing", path, timing }));
}

export async function api<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const method = (init?.method ?? "GET").toUpperCase();
  const cached = cachedGet<T>(path, method);
  if (cached !== undefined) return cached;
  if (await insideForge()) {
    const bridge = await loadBridge();
    let response: { status?: number; body?: unknown };
    try {
      response = (await bridge.invokeRemote({
        path,
        method: (init?.method ?? "GET") as "GET",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: init?.body as never,
      })) as { status?: number; body?: unknown };
    } catch (error) {
      throw new ApiError(error instanceof Error ? error.message : "Forge request failed");
    }
    // invokeRemote resolves to { status, headers, body }, not the body itself.
    let payload: unknown = response?.body;
    if (typeof payload === "string") {
      try {
        payload = payload ? JSON.parse(payload) : {};
      } catch {
        /* leave as text */
      }
    }
    const status = response?.status ?? 200;
    noteTiming(path, (response as { headers?: unknown }).headers);
    if (status >= 400) {
      const message = (payload as { error?: { message?: string } } | undefined)?.error?.message;
      throw new ApiError(message ?? `Request failed (${status})`);
    }
    return rememberGet(path, method, (payload ?? {}) as T);
  }
  const response = await fetch(path, {
    method: init?.method ?? "GET",
    headers: init?.body !== undefined ? { "content-type": "application/json", accept: "application/json" } : { accept: "application/json" },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await response.text();
  const payload = text ? (JSON.parse(text) as { error?: { message?: string } }) : {};
  noteTiming(path, response.headers);
  if (!response.ok) throw new ApiError(payload.error?.message ?? `Request failed (${response.status})`);
  return rememberGet(path, method, payload as T);
}

export async function uploadArtifact(uploadUrl: string, file: Blob, contentType: string): Promise<void> {
  const response = await fetch(uploadUrl, { method: "PUT", headers: { "content-type": contentType }, body: file });
  if (!response.ok) throw new ApiError("The screenshot could not be stored.");
}
